import React, { useState, useRef, useEffect, type ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material'
import {
  Check as CheckIcon,
  Close as CloseIcon,
  CameraAlt as CameraAltIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'
import { DialogHeader } from '@/components/atoms/DialogHeader'
import { RADIUS } from '@/shared/styles/radii'
import {
  NOTION_BUTTON_PRIMARY_SX,
  NOTION_BUTTON_TEXT_SX,
} from '@/shared/styles/buttonStyles'
import { getDispenseAudit, postDispenseAudit } from '../apis/dispenseAuditApi'
import { DispenseAuditResponse, DrugAuditItem } from '../types/dispenseAudit'

type Props = {
  open: boolean
  onClose: () => void
  yakurekiId: string
}

// 鑑査結果 status を Notion 流の subtle icon に揃える.
// 旧: emoji 風 ○△✗ + 鮮やかな legacy palette (status.success/warning/error の MUI 系 hex).
// 新: 機能アイコン (Check/Help/Close) + Notion 28 spec の subtle 系 token (state.successFg / warnIcon / dangerIcon).
const STATUS_CONFIG: Record<
  DrugAuditItem['status'],
  { icon: ReactNode; text: string }
> = {
  match: {
    icon: (
      <CheckIcon sx={{ fontSize: 16, color: colorTokens.state.successFg }} />
    ),
    text: '一致',
  },
  uncertain: {
    icon: (
      <HelpOutlineIcon
        sx={{ fontSize: 16, color: colorTokens.state.warnIcon }}
      />
    ),
    text: '不明確',
  },
  not_found: {
    icon: (
      <CloseIcon sx={{ fontSize: 16, color: colorTokens.state.dangerIcon }} />
    ),
    text: '未検出',
  },
}

export const DispenseAuditDialog: React.FC<Props> = ({
  open,
  onClose,
  yakurekiId,
}) => {
  const [image, setImage] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<DispenseAuditResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ダイアログopen時に過去の鑑査結果を取得
  useEffect(() => {
    if (!open) return
    const fetchPrevious = async () => {
      try {
        const prev = await getDispenseAudit(yakurekiId)
        if (prev) setResult(prev)
      } catch {
        // 取得失敗は無視（新規鑑査フローに進む）
      }
    }
    fetchPrevious()
  }, [open, yakurekiId])

  const handleReset = () => {
    if (preview) URL.revokeObjectURL(preview)
    setImage(null)
    setPreview(null)
    setResult(null)
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (!file.type.startsWith('image/')) {
        setError('画像ファイルを選択してください')
        return
      }
      if (preview) URL.revokeObjectURL(preview)
      setImage(file)
      setPreview(URL.createObjectURL(file))
      setError(null)
    }
  }

  const handleSubmit = async () => {
    if (!image) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await postDispenseAudit(yakurekiId, image)
      setResult(response)
    } catch {
      setError('鑑査処理に失敗しました。もう一度お試しください。')
    } finally {
      setIsLoading(false)
    }
  }

  const renderDrugItem = (drug: DrugAuditItem, index: number) => {
    const config = STATUS_CONFIG[drug.status]
    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          py: 1,
          px: 1.5,
          borderBottom:
            index < (result?.drugs.length ?? 0) - 1
              ? `1px solid ${colorTokens.border.default}`
              : 'none',
        }}
      >
        <Box sx={{ flexShrink: 0, mt: 0.25 }}>{config.icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2">{drug.drug_name}</Typography>
          {drug.note && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {drug.note}
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  const renderResult = () => {
    if (!result) return null
    const { summary } = result

    return (
      <Box>
        {/* サマリー — Notion 流 inline caption */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1.25 }}
        >
          {summary.total} 件中 ・ 一致 {summary.match} ・ 不明確{' '}
          {summary.uncertain} ・ 未検出 {summary.not_found}
        </Typography>

        {/* 薬品リスト */}
        <Box
          sx={{
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: `${RADIUS.md}px`,
            overflow: 'hidden',
          }}
        >
          {result.drugs.map((drug, index) => renderDrugItem(drug, index))}
        </Box>

        {/* 余剰薬品 */}
        {result.excess_drugs.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
            >
              処方外の薬品（写真にのみ存在）
            </Typography>
            <Stack spacing={0} sx={{ pl: 1 }}>
              {result.excess_drugs.map((drug, index) => (
                <Typography key={index} variant="body2" color="text.secondary">
                  ・{drug}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
    >
      <DialogHeader
        title="調剤鑑査"
        onClose={handleClose}
        disabled={isLoading}
      />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Typography variant="body2" color="error.main" sx={{ mb: 1.5 }}>
            {error}
          </Typography>
        )}

        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              gap: 1.5,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">
              確認中...
            </Typography>
          </Box>
        )}

        {!isLoading && result && renderResult()}

        {!isLoading && !result && (
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {preview ? (
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  component="img"
                  src={preview}
                  alt="撮影画像"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: `${RADIUS.md}px`,
                    border: `1px solid ${colorTokens.border.default}`,
                    objectFit: 'contain',
                  }}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ ...NOTION_BUTTON_TEXT_SX, mt: 1 }}
                >
                  撮り直す
                </Button>
              </Box>
            ) : (
              // Notion 流の clickable area: bg.subtle + 細い border でボタンらしさを担保.
              // hover で border 強調 + bg.muted に変化 → 「押せる」 affordance を強める.
              <Box
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    fileInputRef.current?.click()
                  }
                }}
                sx={{
                  borderRadius: `${RADIUS.md}px`,
                  bgcolor: colorTokens.bg.subtle,
                  border: `1px solid ${colorTokens.border.default}`,
                  py: 4,
                  px: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 120ms, border-color 120ms',
                  '&:hover': {
                    bgcolor: colorTokens.bg.muted,
                    borderColor: colorTokens.fg.faint,
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    borderColor: colorTokens.accent.primary,
                    boxShadow: colorTokens.focus.ringShadow,
                  },
                }}
              >
                <CameraAltIcon
                  sx={{ fontSize: 28, color: 'text.secondary', mb: 0.5 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: 'text.primary' }}
                >
                  タップして医薬品を撮影
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  PTPシート・薬袋・ラベルが写るように
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {result ? (
          <>
            <Button
              onClick={handleReset}
              variant="text"
              size="small"
              sx={NOTION_BUTTON_TEXT_SX}
            >
              再鑑査
            </Button>
            <Button
              onClick={handleClose}
              variant="contained"
              size="small"
              sx={NOTION_BUTTON_PRIMARY_SX}
            >
              閉じる
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              variant="text"
              size="small"
              disabled={isLoading}
              sx={NOTION_BUTTON_TEXT_SX}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="small"
              disabled={!image || isLoading}
              sx={NOTION_BUTTON_PRIMARY_SX}
            >
              鑑査開始
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
